import { Component, computed, inject, Input } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { NbDialogService, NbIconConfig } from '@nebular/theme';
import { AttachmentMergeDto } from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { Attachment, attachmentsFromDetails, attachmentToMerge } from '@shared/form-controls/attachment-upload/attachment-utils';

export type RepairTaskCloseMode = 'finish' | 'decline';

export interface RepairTaskCloseResult {
  comment: string;
  attachments: AttachmentMergeDto[];
}

export async function openRepairTaskCloseDialog(
  dialogService: NbDialogService,
  mode: RepairTaskCloseMode,
  taskTitle: string,
  requirePhoto = true,
): Promise<RepairTaskCloseResult | undefined> {
  const ref = dialogService.open(RepairTaskCloseDialogComponent, {
    context: { mode, taskTitle, requirePhoto },
  });
  const result = await firstValueFrom(ref.onClose);
  return result ? result as RepairTaskCloseResult : undefined;
}

@Component({
  selector: 'wr-repair-task-close-dialog',
  standalone: false,
  templateUrl: './repair-task-close-dialog.component.html',
  styleUrls: ['./repair-task-close-dialog.component.scss'],
})
export class RepairTaskCloseDialogComponent {
  private readonly formBuilder = inject(FormBuilder);

  @Input() mode: RepairTaskCloseMode = 'finish';
  @Input() taskTitle = '';
  @Input() requirePhoto = true;

  readonly warningIcon: NbIconConfig = { icon: 'alert-triangle-outline', pack: 'eva' };

  readonly form = this.formBuilder.group({
    comment: this.formBuilder.nonNullable.control(''),
    attachments: this.formBuilder.nonNullable.control<Attachment[]>(attachmentsFromDetails([])),
  });

  private readonly value = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });

  readonly hasComment = computed(() => (this.value().comment ?? '').trim().length > 0);
  readonly hasPhoto = computed(() => (this.value().attachments ?? []).some(a => !a.deleted));
  readonly photoMissing = computed(() => this.mode === 'finish' && this.requirePhoto && !this.hasPhoto());
  readonly canSubmit = computed(() => this.hasComment() && !this.photoMissing());

  get header(): string {
    return this.mode === 'finish' ? 'Reparaturauftrag abschließen' : 'Reparaturauftrag stornieren';
  }

  submit(): RepairTaskCloseResult | false {
    if (!this.canSubmit()) {
      return false;
    }
    const { comment, attachments } = this.form.getRawValue();
    return {
      comment: comment.trim(),
      attachments: this.mode === 'finish'
        ? attachments.filter(a => !a.deleted).map(a => attachmentToMerge(a))
        : [],
    };
  }
}
